import {Box, Chip, Divider, Stack, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {IndividualPlanAiGeneratedDiagnostic} from '../../../../../../../backend/responses/individual-plan-ai-generated';
import {translations} from '../../../../../../../i18n/translations';

/**
 * Read-only view of the diagnostics of an individual plan, with their goals, objectives and cares.
 * Everything is rendered as plain text. Used to preview AI proposals and previous versions of a plan.
 */
export const IndividualPlanDiagnosticsView = ({
  diagnostics,
}: {
  diagnostics: Array<IndividualPlanAiGeneratedDiagnostic>;
}) => {
  const {t} = useTranslation();
  const labels = translations.seniors.edit.senior_dcm.individual_plan;

  return (
    <Stack gap="16px">
      {diagnostics.map((diagnostic, diagnosticIndex) => (
        <Box key={`diagnostic-${diagnosticIndex}`}>
          <Typography sx={{fontWeight: 600, fontSize: '16px'}}>
            {t(labels.diagnostic)}: {diagnostic.definition}
          </Typography>
          {diagnostic.goals?.map((goal, goalIndex) => (
            <Box key={`goal-${goalIndex}`} sx={{marginTop: '12px', marginLeft: '16px'}}>
              <Typography sx={{fontWeight: 600, fontSize: '15px'}}>
                {t(labels.goal)}: {goal.definition}
              </Typography>
              {goal.objectives?.map((objective, objectiveIndex) => (
                <Box
                  key={`objective-${objectiveIndex}`}
                  sx={{marginTop: '8px', marginLeft: '16px'}}
                >
                  <Typography sx={{fontSize: '14px'}}>
                    {t(labels.add_diagnostic_goal_objective.table.definition)}:{' '}
                    {objective.definition}
                  </Typography>
                  {!!objective.activityTypesNames?.length && (
                    <Stack gap="6px" direction="row" flexWrap="wrap" sx={{marginTop: '6px'}}>
                      {objective.activityTypesNames.map((name, nameIndex) => (
                        <Chip key={`activity-${nameIndex}`} size="small" label={name} />
                      ))}
                    </Stack>
                  )}
                  {!!objective.cares?.length && (
                    <>
                      {!!objective.activityTypesNames?.length && (
                        <Divider sx={{marginTop: '6px', marginBottom: '6px'}} />
                      )}
                      <Stack
                        gap="6px"
                        direction="row"
                        flexWrap="wrap"
                        sx={{marginTop: objective.activityTypesNames?.length ? 0 : '6px'}}
                      >
                        {objective.cares.map((care, careIndex) => (
                          <Chip
                            key={`care-${careIndex}`}
                            size="small"
                            variant="outlined"
                            label={care.definition}
                            sx={{
                              height: 'auto',
                              maxWidth: '100%',
                              '& .MuiChip-label': {
                                whiteSpace: 'normal',
                                paddingTop: '4px',
                                paddingBottom: '4px',
                              },
                            }}
                          />
                        ))}
                      </Stack>
                    </>
                  )}
                </Box>
              ))}
            </Box>
          ))}
        </Box>
      ))}
    </Stack>
  );
};
